/**
 * Landing sidebar mini cards — compact versions of NewMembers,
 * FeaturedMembers, SuccessStories, KennelClubs, Partners, and CTA so the
 * center feed strip stops running 4000px tall.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Users,
  Crown,
  Sparkles,
  Heart,
  Globe,
  Handshake,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SidebarLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{children}</p>
);

// ---------------------------------------------------------------------------
// New Members — condensed avatar row (up to 6 avatars, count + View all)
// ---------------------------------------------------------------------------
export const NewMembersMini = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/members/recent?limit=6`);
        if (res.ok) {
          const data = await res.json();
          setMembers(data.members || []);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <Card><CardContent className="p-4"><Skeleton className="h-14 w-full" /></CardContent></Card>
    );
  }
  if (!members.length) return null;

  return (
    <Card data-testid="sidebar-new-members">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <SidebarLabel>New Members</SidebarLabel>
          <button
            onClick={() => navigate('/members')}
            className="text-[10px] text-primary hover:underline"
            data-testid="sidebar-new-members-view-all"
          >
            View all →
          </button>
        </div>
        <div className="flex -space-x-2 mb-2">
          {members.slice(0, 6).map((m) => (
            <button
              key={m.id || m.username}
              onClick={() => navigate(`/profile/${m.username}`)}
              className="w-8 h-8 rounded-full ring-2 ring-white overflow-hidden bg-amber-100 flex items-center justify-center text-[11px] font-semibold text-amber-800 hover:z-10 hover:scale-110 transition-transform"
              title={m.display_name || m.username}
            >
              {m.avatar_url ? (
                <img src={m.avatar_url} alt={m.username} className="w-full h-full object-cover" />
              ) : (
                (m.display_name || m.username || 'U').slice(0, 1).toUpperCase()
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Welcome {members.length} new member{members.length === 1 ? '' : 's'} — say hi 👋
        </p>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Featured Members — 3 rows with avatar + display name
// ---------------------------------------------------------------------------
export const FeaturedMembersMini = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/promo-pages/featured`, { params: { limit: 3 } });
        setMembers(res.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <Card><CardContent className="p-4"><Skeleton className="h-24 w-full" /></CardContent></Card>;
  if (!members.length) return null;

  return (
    <Card data-testid="sidebar-featured-members">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-3.5 h-3.5 text-amber-600" />
          <SidebarLabel>Featured Members</SidebarLabel>
        </div>
        <ul className="space-y-2.5">
          {members.map((m) => (
            <li key={m.id || m.username}>
              <button
                onClick={() => navigate(`/profile/${m.username}`)}
                className="flex items-center gap-2 w-full text-left group"
                data-testid={`sidebar-featured-member-${m.username}`}
              >
                <div className="w-9 h-9 rounded-full overflow-hidden bg-amber-100 flex items-center justify-center text-xs font-semibold text-amber-800 shrink-0">
                  {m.avatar_url ? (
                    <img src={m.avatar_url} alt={m.username} className="w-full h-full object-cover" />
                  ) : (
                    (m.display_name || m.username || 'U').slice(0, 1).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {m.display_name || m.username}
                  </p>
                  {m.tagline && (
                    <p className="text-[11px] text-muted-foreground truncate">{m.tagline}</p>
                  )}
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Success Stories — 2 short cards, tap for full page
// ---------------------------------------------------------------------------
export const SuccessStoriesMini = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/adopter-cards/recognitions/recent?limit=2`);
        setStories(res.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <Card><CardContent className="p-4"><Skeleton className="h-24 w-full" /></CardContent></Card>;
  if (!stories.length) return null;

  return (
    <Card data-testid="sidebar-success-stories">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-3.5 h-3.5 text-rose-500" />
          <SidebarLabel>Success Stories</SidebarLabel>
        </div>
        <ul className="space-y-3">
          {stories.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => navigate('/success-stories')}
                className="text-left w-full group"
                data-testid={`sidebar-success-story-${s.id}`}
              >
                <p className="text-sm font-medium line-clamp-1 group-hover:text-primary transition-colors">
                  {s.pet_name || s.adopter_name || 'Happy adoption'}
                </p>
                {s.story_snippet || s.short_story ? (
                  <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                    {s.story_snippet || s.short_story}
                  </p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 text-xs"
          onClick={() => navigate('/success-stories')}
          data-testid="sidebar-success-stories-more"
        >
          Read more stories <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Kennel Clubs directory — one-liner + Open button
// ---------------------------------------------------------------------------
export const KennelClubsMini = () => {
  const navigate = useNavigate();
  return (
    <Card
      className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
      data-testid="sidebar-kennel-clubs"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-2 mb-2">
          <Globe className="w-4 h-4 text-amber-700 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">Kennel Clubs Directory</p>
            <p className="text-[11px] text-muted-foreground">121 clubs worldwide, FCI-tagged</p>
          </div>
        </div>
        <Button
          size="sm"
          className="w-full bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
          onClick={() => navigate('/kennel-clubs')}
          data-testid="sidebar-kennel-clubs-open"
        >
          Open directory
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Partners — condensed "Partner with us" ask
// ---------------------------------------------------------------------------
export const PartnersMini = () => {
  const navigate = useNavigate();
  return (
    <Card data-testid="sidebar-partners">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Handshake className="w-3.5 h-3.5 text-primary" />
          <SidebarLabel>Partner With Us</SidebarLabel>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Sponsors, brands, rescues — reach an engaged pet-owner community. Founding sponsor slots are open.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="w-full h-8 text-xs"
          onClick={() => navigate('/contact?type=partner')}
          data-testid="sidebar-partners-cta"
        >
          Say hello →
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// CTA — Join the pack (mini)
// ---------------------------------------------------------------------------
export const JoinPackMini = ({ user }) => {
  const navigate = useNavigate();
  if (user) return null; // already a member — skip the pitch
  return (
    <Card
      className="bg-gradient-to-br from-amber-600 to-amber-700 text-white border-0"
      data-testid="sidebar-join-pack"
    >
      <CardContent className="p-4 text-center">
        <Sparkles className="w-5 h-5 mx-auto mb-1.5" />
        <p className="text-sm font-semibold mb-1">Join Our Pack</p>
        <p className="text-[11px] text-white/80 mb-3">
          Founding Members lock in early-member pricing.
        </p>
        <Button
          size="sm"
          className="w-full h-8 bg-white text-amber-800 hover:bg-white/90 text-xs font-semibold"
          onClick={() => navigate('/register')}
          data-testid="sidebar-join-pack-cta"
        >
          <Users className="w-3.5 h-3.5 mr-1" /> Get started free
        </Button>
      </CardContent>
    </Card>
  );
};
